import { isTempClipId } from '@src/services/WriteQueueService';
import { LogManager } from '@src/utility/Logger';
import TurndownService from 'turndown';
import { v4 as uuid } from 'uuid';

import {
  ALBUM_SHARED_CONTENTS_QUERY,
  DASHBOARD_ALBUM_QUERY,
} from '../gql/album-gql';
import { PERSON_BY_EMAIL_QUERY } from '../gql/person-gql';
import { STORY_QUERY, STORY_SPEAKERS_QUERY } from '../gql/story-gql';
import { request } from '../utility/dato';
import {
  parseAiResponseBlob,
  populateStoryQueryWithMissingFields,
  type TopicParagraphMapping,
} from '../utility/story';
import transformKeysToCamelCase from '../utility/transformKeysToCamelCase';
import { isStaleVersionError } from './isStaleVersionError';

import type ApiClient from '../apiClient/ApiClient';
import type { TalkingPointContent } from '../types.ts/general';
import type {
  AlbumStory,
  Artifact,
  DatoAlbumQueryResult,
  DatoStory,
  DatoStoryQueryResult,
  DatoStorySpeakersQueryResult,
  Person,
  SharedContent,
  Story,
  StoryDTO,
} from '../types.ts/story';
import type { DatoSpeaker } from './SpeakerRepository';
import type { Client, SimpleSchemaTypes } from '@datocms/cma-client-browser';
import type { GraphQLClient } from 'graphql-request';

const turndownService = new TurndownService();
const logger = LogManager.getLogger('repository:StoryRepository');

const MAX_OPTIMISTIC_LOCK_RETRIES = 3;

export class StoryRepository {
  private dClient: Client | ApiClient;
  private gqlClient: GraphQLClient;
  private directDatoClient: Client | null;

  constructor(
    dClient: Client | ApiClient,
    gqlClient: GraphQLClient,
    directDatoClient?: Client,
  ) {
    this.dClient = dClient;
    this.gqlClient = gqlClient;
    this.directDatoClient = directDatoClient || null;
  }

  async findPersonByEmail(email: string): Promise<Person | null> {
    const response = await this.gqlClient.request<{ allPeople: Person[] }>(
      PERSON_BY_EMAIL_QUERY,
      { email },
    );
    return response.allPeople?.[0] || null;
  }

  async createPerson(params: {
    name: string;
    email: string;
    title?: string;
  }): Promise<Person> {
    const datoClient = this.directDatoClient || this.dClient;
    const itemType = await datoClient.itemTypes.find('person');
    const newPerson = await datoClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      name: params.name,
      email: params.email,
      title: params.title || '',
    });
    return newPerson as unknown as Person;
  }

  // TODO: Should migrate to using DatoClientStore gqlClient
  // but currently relies on 'includeDrafts' and 'excludeInvalid' options
  // which are required at client creation.
  async findMany(
    albumId: string,
    clientOptions: {
      includeDrafts: boolean;
      excludeInvalid: boolean;
      environment: string;
    },
  ): Promise<AlbumStory[]> {
    const response = (await request({
      query: DASHBOARD_ALBUM_QUERY,
      variables: {
        id: albumId,
      },
      ...clientOptions,
    })) as Record<'showcase', DatoAlbumQueryResult>;

    if (!response?.showcase?.stories) return [];

    return response.showcase.stories;
  }

  // Story-level shared contents for every story in the album, keyed by story
  // id. Deferred companion to findMany — see ALBUM_SHARED_CONTENTS_QUERY.
  async findManySharedContents(
    albumId: string,
    clientOptions: {
      includeDrafts: boolean;
      excludeInvalid: boolean;
      environment: string;
    },
  ): Promise<Record<string, SharedContent[]>> {
    const response = (await request({
      query: ALBUM_SHARED_CONTENTS_QUERY,
      variables: {
        id: albumId,
      },
      ...clientOptions,
    })) as Record<
      'showcase',
      {
        stories?: {
          id: string;
          _allReferencingSharedContents?: SharedContent[];
        }[];
      }
    >;

    const byStoryId: Record<string, SharedContent[]> = {};
    for (const story of response?.showcase?.stories ?? []) {
      byStoryId[story.id] = story._allReferencingSharedContents ?? [];
    }
    return byStoryId;
  }

  async findOne(id: string): Promise<Story | null> {
    const response = (await this.gqlClient.request(STORY_QUERY, {
      id,
    })) as DatoStoryQueryResult;

    if (!response.story) return null;
    const fullStory = await populateStoryQueryWithMissingFields(response.story);

    const storyClips = response.story.otherVideos?.map(clip => ({
      ...clip,
    }));

    const story: Story = {
      ...fullStory!,
      otherVideos: storyClips || [],
      allVideos: storyClips || [],
      // sharableImages: sharableImages || [],
    };
    return story;
  }

  async create(story: Partial<StoryDTO>): Promise<DatoStory> {
    if (!story.title) {
      throw new Error('Story title is required');
    }

    // Handle storyteller - either by ID or by email lookup
    let storyTellerId = story.storyTeller?.id;

    if (!storyTellerId && story.storyTeller?.email) {
      const existingPerson = await this.findPersonByEmail(
        story.storyTeller.email,
      );

      if (existingPerson) {
        storyTellerId = existingPerson.id;
      } else {
        throw new Error('Could not find existing person!');
      }
    }

    if (!storyTellerId) {
      throw new Error('Storyteller id is required');
    }

    const uploadLogitemType =
      await this.dClient.itemTypes.find('story_steps_log');
    const storyUploadLog = await this.dClient.items.create({
      item_type: { type: 'item_type', id: uploadLogitemType.id },
      story_id: story.id,
    });

    const itemType = await this.dClient.itemTypes.find('story');
    const createdItem = await this.dClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      hash: uuid(),
      slug: uuid(),
      upload_log: storyUploadLog.id,
      ...(story.title && {
        title: { en: story.title },
      }),
      ...(story.storyType && {
        story_type: story.storyType,
      }),
      ...(story.byExternalUser != null && {
        by_external_user: story.byExternalUser,
      }),
      ...(story.externalUploadLog && {
        external_upload_log: JSON.stringify(story.externalUploadLog),
      }),
      story_teller: storyTellerId,
      ...(story.primaryShowcase?.id && {
        primary_showcase: story.primaryShowcase.id,
      }),
      // force AWS usage for all newly created stories
      use_aws: true,
    });

    if (story.primaryShowcase?.id) {
      await this.addAsReferenceToShowcase(
        createdItem.id,
        story.primaryShowcase.id,
      );
    }

    return this.itemToStory({ ...createdItem, ...story });
  }

  private async addAsReferenceToShowcase(storyId: string, showcaseId: string) {
    for (let attempt = 0; attempt <= MAX_OPTIMISTIC_LOCK_RETRIES; attempt++) {
      const record = await this.dClient.items.find(showcaseId);
      const currentStories = (record.stories as string[]) || [];

      if (currentStories.includes(storyId)) {
        return;
      }

      try {
        await this.dClient.items.update(showcaseId, {
          stories: [...currentStories, storyId],
          meta: {
            current_version: record.meta.current_version,
          },
        });
        await this.dClient.items.publish(showcaseId);
        return;
      } catch (error) {
        if (
          isStaleVersionError(error) &&
          attempt < MAX_OPTIMISTIC_LOCK_RETRIES
        ) {
          logger.warn('Stale version adding story to showcase, retrying', {
            showcaseId,
            storyId,
            attempt,
          });
          continue;
        }
        throw error;
      }
    }
  }

  // One read-modify-write for all ids: concurrent per-story writes to the
  // same showcase collide on its version lock (PLAT-11807).
  async removeReferencesFromShowcase(storyIds: string[], showcaseId: string) {
    const idSet = new Set(storyIds);
    for (let attempt = 0; attempt <= MAX_OPTIMISTIC_LOCK_RETRIES; attempt++) {
      const record = await this.dClient.items.find(showcaseId);
      const currentStories = (record.stories as string[]) || [];
      const remainingStories = currentStories.filter(id => !idSet.has(id));

      if (remainingStories.length === currentStories.length) {
        return;
      }

      try {
        await this.dClient.items.update(showcaseId, {
          stories: remainingStories,
          meta: {
            current_version: record.meta.current_version,
          },
        });
        return;
      } catch (error) {
        if (
          isStaleVersionError(error) &&
          attempt < MAX_OPTIMISTIC_LOCK_RETRIES
        ) {
          logger.warn(
            'Stale version removing stories from showcase, retrying',
            {
              showcaseId,
              storyIds,
              attempt,
            },
          );
          continue;
        }
        throw error;
      }
    }
  }

  async getStorySpeakers(storyId: string): Promise<DatoSpeaker[]> {
    const response = await this.gqlClient.request<DatoStorySpeakersQueryResult>(
      STORY_SPEAKERS_QUERY,
      {
        id: storyId,
      },
    );
    return response?.story?.speakers || [];
  }

  async updateStoryStepsLog(uploadLog: Record<string, any>): Promise<void> {
    await this.dClient.items.update(uploadLog.id, uploadLog);
  }

  /**
   * Narrow write that merges a new `topicParagraphMapping` key into the
   * story's existing `ai_response` JSON blob. Re-reads ai_response immediately
   * before write so concurrent edits to other ai_response keys (e.g.
   * `responses` from content generation) are not clobbered.
   */
  async updateTopicParagraphMapping(
    storyId: string,
    mapping: TopicParagraphMapping,
  ): Promise<void> {
    for (let attempt = 0; attempt <= MAX_OPTIMISTIC_LOCK_RETRIES; attempt++) {
      const record = await this.dClient.items.find(storyId);
      const merged = {
        ...parseAiResponseBlob(record.ai_response, storyId),
        topicParagraphMapping: mapping,
      };

      try {
        await this.dClient.items.update(storyId, {
          ai_response: JSON.stringify(merged),
          meta: {
            current_version: record.meta.current_version,
          },
        });
        await this.dClient.items.publish(storyId);
        return;
      } catch (error) {
        if (
          isStaleVersionError(error) &&
          attempt < MAX_OPTIMISTIC_LOCK_RETRIES
        ) {
          logger.warn(
            'Stale version updating topicParagraphMapping, retrying',
            { storyId, attempt },
          );
          continue;
        }
        throw error;
      }
    }
  }

  async update(story: Partial<StoryDTO> & { id: string }): Promise<DatoStory> {
    if (!story.id) {
      throw new Error('Story id is required');
    }

    const updatedItem = await this.dClient.items.update(story.id, {
      ...(story.title && {
        title: { en: story.title },
      }),

      ...(story.description && {
        description: { en: story.description },
      }),

      // DO NOT OVERWRITE VIDEOS TO AVOID DELETING THEM
      // Instead, videos are added and removed directly with addVideoToStory and removeVideoFromStory
      // ...(story.otherVideos && {
      //   other_videos: story.otherVideos.map((video) => video.id),
      // }),

      ...(story.finalVideo && {
        final_video: story.finalVideo.id,
      }),

      ...(story.shareableImages && {
        shareable_images: story.shareableImages.map(s => s.id),
      }),

      ...(story.aiPhotos && {
        ai_photos: await Promise.all(
          story.aiPhotos.map(photo => ({ upload_id: photo.id })),
        ),
      }),

      ...(story.storyAssets && {
        story_assets: await Promise.all(
          story.storyAssets.map(photo => ({
            upload_id: photo.id,
            ...((photo as Artifact).title && {
              title: (photo as Artifact).title,
            }),
          })),
        ),
      }),

      ...(story.storyArtifacts && {
        story_artifacts: await Promise.all(
          story.storyArtifacts.map(photo => ({
            upload_id: photo.id,
            ...((photo as Artifact).title && {
              title: (photo as Artifact).title,
            }),
          })),
        ),
      }),

      ...(story.storyArtifactsVideo && {
        story_artifacts_video: await Promise.all(
          story.storyArtifactsVideo.map(video => ({
            upload_id: video.id,
            ...((video as Artifact).title && {
              title: (video as Artifact).title,
            }),
          })),
        ),
      }),

      ...(story.contributors && {
        contributors: story.contributors.map(c => c.id),
      }),

      ...(story.myAudios && {
        my_audios: story.myAudios.map(audio => audio.id),
      }),

      ...(story.aiResponse && {
        ai_response: JSON.stringify(story.aiResponse),
      }),

      ...(story.originalVideoLanguage && {
        original_video_language: story.originalVideoLanguage,
      }),

      ...(story.externalUploadStatus && {
        external_upload_status: story.externalUploadStatus,
      }),
    });
    await this.dClient.items.publish(story.id);

    return this.itemToStory(updatedItem);
  }

  async addVideoToStory(storyId: string, videoId: string) {
    await this.addMultipleVideosToStory(storyId, [videoId]);
  }

  async addMultipleVideosToStory(storyId: string, videoIds: string[]) {
    const tempIds = videoIds.filter(isTempClipId);
    if (tempIds.length > 0) {
      logger.warn(
        'addMultipleVideosToStory: filtering out unresolved temp clip IDs',
        { storyId, tempIds },
      );
    }
    const resolvedIds = videoIds.filter(id => !isTempClipId(id));
    if (resolvedIds.length === 0) {
      logger.warn(
        'addMultipleVideosToStory: no valid video IDs after filtering',
        { storyId },
      );
      return;
    }

    const story = await this.dClient.items.find(storyId);
    const existing = (story.other_videos as string[]) || [];

    // `other_videos` is a plain link array with no uniqueness constraint, and
    // this method is called from several paths (and, before the write-queue fix
    // above it, twice for one batch). Dropping IDs the story already links —
    // and collapsing repeats within this call — makes a repeated call
    // idempotent instead of doubling the clip in the sidebar.
    const linked = new Set(existing);
    const toAdd = resolvedIds.filter(id => {
      if (linked.has(id)) return false;
      linked.add(id);
      return true;
    });

    if (toAdd.length === 0) {
      logger.debug('addMultipleVideosToStory: all videos already linked', {
        storyId,
        videoIds: resolvedIds,
      });
      return;
    }

    await this.dClient.items.update(storyId, {
      other_videos: toAdd.concat(existing),
    });
    await this.dClient.items.publish(storyId);
  }

  /**
   * Removes several videos from a story in a single write (PLAT-11118).
   *
   * Calling `removeVideoFromStory` in a loop costs three round trips per video
   * and, worse, is a read-modify-write: two of them in flight together both
   * read the same pre-delete `other_videos` and the last write puts the other's
   * video back. Filtering every id out of one read makes that race
   * unrepresentable rather than merely avoided, and the cost stops scaling with
   * the size of the selection.
   */
  async removeVideosFromStory(storyId: string, videoIds: string[]) {
    const removing = new Set(videoIds.filter(id => !isTempClipId(id)));
    if (removing.size === 0) {
      logger.warn('removeVideosFromStory: no valid video IDs after filtering', {
        storyId,
      });
      return;
    }

    const story = await this.dClient.items.find(storyId);
    const finalVideo = story.final_video as string | null;
    await this.dClient.items.update(storyId, {
      other_videos: ((story.other_videos as string[]) || []).filter(
        id => !removing.has(id),
      ),
      final_video: finalVideo && removing.has(finalVideo) ? null : finalVideo,
    });
    await this.dClient.items.publish(storyId);
  }

  /**
   * Removes pull quotes (shareable images) from a story in a single write.
   *
   * Deleting a quote is an unlink, not a record destroy: `items.destroy` is a
   * DELETE, and the arbor-v1 Dato proxy (`pages/api/dato/[...path].ts`) only
   * routes GET/POST/PUT for `items` — a DELETE falls through and answers
   * `200 {}`, so the caller sees success and the quote survives. Batched by
   * story for the same read-modify-write reason as `removeVideosFromStory`.
   */
  async removeShareableImagesFromStory(storyId: string, imageIds: string[]) {
    const removing = new Set(imageIds);
    if (removing.size === 0) {
      logger.warn('removeShareableImagesFromStory: no image IDs given', {
        storyId,
      });
      return;
    }

    const story = await this.dClient.items.find(storyId);
    await this.dClient.items.update(storyId, {
      shareable_images: ((story.shareable_images as string[]) || []).filter(
        id => !removing.has(id),
      ),
    });
    await this.dClient.items.publish(storyId);
  }

  async removeVideoFromStory(storyId: string, videoId: string) {
    const story = await this.dClient.items.find(storyId);
    await this.dClient.items.update(storyId, {
      other_videos: (story.other_videos as string[]).filter(
        id => id !== videoId,
      ),
      final_video: story.final_video === videoId ? null : story.final_video,
    });
    await this.dClient.items.publish(storyId);
  }

  private itemToStory(item: SimpleSchemaTypes.Item): DatoStory {
    return {
      ...transformKeysToCamelCase(item),
      title:
        typeof item.title === 'string'
          ? item.title
          : (item.title as { en?: string })?.en,
      _publishedAt: item.meta?.published_at,
    };
  }

  async handleSaveAiGeneratedContent(
    storyId: string,
    data: any,
    type:
      | 'saved_blog'
      | 'saved_social_post'
      | 'saved_email'
      | 'saved_talking_point_content'
      | 'saved_segments',
  ) {
    const fieldMap = {
      saved_blog: 'saved_blog',
      saved_social_post: 'saved_social_post',
      saved_email: 'saved_email',
      saved_talking_point_content: 'saved_talking_point_content',
      saved_segments: 'saved_segments',
    };
    const field = fieldMap[type];
    if (!field) {
      throw new Error(`Invalid content type: ${type}`);
    }
    await this.dClient.items.update(storyId, {
      [field]: JSON.stringify(data, null, 2),
    });
    await this.dClient.items.publish(storyId);
  }

  async saveSavedSegments(
    story: Pick<Story, 'id' | 'savedSegments'>,
    segments: Story['savedSegments'],
  ) {
    await this.handleSaveAiGeneratedContent(
      story.id,
      segments || {},
      'saved_segments',
    );
    return segments;
  }

  async saveTalkingPointContent(
    story: Pick<Story, 'id' | 'savedTalkingPointContent'>,
    data: Record<
      keyof TalkingPointContent,
      Record<'title' | 'content' | 'prompt', string>
    >,
    title: string,
  ) {
    const savedData = story?.savedTalkingPointContent || {};
    let existingKey: number | undefined = undefined;

    for (let [key, content_data] of Object.entries(savedData)) {
      const hasMatch = Object.entries(content_data.content).every(
        ([key, value]) => {
          const k = key as keyof TalkingPointContent;
          const enterContentMk = turndownService.turndown(data[k].content);
          const existingMk = turndownService.turndown(value.content);

          return existingMk === enterContentMk && value.title === data[k].title;
        },
      );
      if (hasMatch) {
        existingKey = Number(key);
        break;
      }
    }

    if (existingKey) {
      delete savedData[existingKey];
    }

    savedData[new Date().getTime()] = { title, content: data };
    await this.handleSaveAiGeneratedContent(
      story.id,
      savedData,
      'saved_talking_point_content',
    );
    return savedData;
  }

  /**
   * Autosave variant of saveBlogOrEmail. Upserts the current draft content
   * onto the existing entry identified by `replaceKey` without flipping
   * `manuallySaved` or touching `version_history`. Intended to be called
   * frequently (debounced) from the autosave hook.
   *
   * Versioning (named + AI checkpoints) goes through saveBlogOrEmail
   * + the existing `onVersionSaved` callback path, so this stays narrow.
   */
  async autosaveBlogOrEmail(
    story: Pick<Story, 'id' | 'savedBlog' | 'savedSocialPost' | 'savedEmail'>,
    data: string,
    title: string,
    userName: string | null | undefined,
    type: 'saved_blog' | 'saved_social_post' | 'saved_email',
    replaceKey: number,
  ) {
    const savedData =
      (type === 'saved_email'
        ? story.savedEmail
        : type === 'saved_blog'
          ? story.savedBlog
          : story.savedSocialPost) || {};

    const existing = savedData[replaceKey];
    if (!existing) {
      logger.warn(
        'autosaveBlogOrEmail: no entry found for replaceKey, skipping',
        { replaceKey, type, storyId: story.id },
      );
      return null;
    }

    const username = userName ?? existing.username ?? 'Arbor Admin';
    savedData[replaceKey] = {
      ...existing,
      title,
      content: data,
      username,
    };

    await this.handleSaveAiGeneratedContent(story.id, savedData, type);
    return savedData;
  }

  async saveBlogOrEmail(
    story: Pick<Story, 'id' | 'savedBlog' | 'savedSocialPost' | 'savedEmail'>,
    data: string,
    title: string,
    userName: string | null | undefined,
    type: 'saved_blog' | 'saved_social_post' | 'saved_email',
    replaceKey?: number,
    manuallySaved?: boolean,
    /**
     * Social posts only: distribution channel the post was generated for
     * (PLAT-11038), persisted so reopening restores the channel's preview.
     */
    channelSlug?: string | null,
  ) {
    let savedData =
      type === 'saved_email'
        ? story.savedEmail
        : type === 'saved_blog'
          ? story.savedBlog
          : story.savedSocialPost;

    savedData = savedData || {};

    let shouldMarkSaved = manuallySaved;
    // A save rewrites the entry under a fresh timestamp key, so anything not
    // passed in has to be carried over from the entry being replaced.
    let existingChannelSlug: string | undefined;
    const readChannelSlug = (entry: unknown) =>
      (entry as { channelSlug?: string } | undefined)?.channelSlug;

    if (replaceKey !== undefined && savedData[replaceKey]) {
      if (savedData[replaceKey].manuallySaved) {
        shouldMarkSaved = true;
      }
      existingChannelSlug = readChannelSlug(savedData[replaceKey]);
      delete savedData[replaceKey];
    } else {
      const currDataMarkdown = turndownService.turndown(data);

      let existingKey: number | undefined = undefined;

      for (const [key, entry] of Object.entries(savedData)) {
        const dataMarkdown = turndownService.turndown(entry.content);
        if (currDataMarkdown === dataMarkdown) {
          existingKey = Number(key);
          break;
        }
      }

      if (existingKey) {
        existingChannelSlug = readChannelSlug(savedData[existingKey]);
        delete savedData[existingKey];
      }
    }

    const username = userName ?? 'Arbor Admin';
    // `?? existing` (not `||`) so an explicit "no channel" never discards a slug
    // already stored on the post.
    const resolvedChannelSlug = channelSlug ?? existingChannelSlug;
    savedData[new Date().getTime()] = {
      title,
      content: data,
      username,
      ...(shouldMarkSaved ? { manuallySaved: true } : {}),
      ...(type === 'saved_social_post' && resolvedChannelSlug
        ? { channelSlug: resolvedChannelSlug }
        : {}),
    };
    await this.handleSaveAiGeneratedContent(story.id, savedData, type);
    return savedData;
  }

  public async createShareableImage(
    story: Pick<Story, 'id' | 'title' | 'storyTeller'>,
    quote: string,
    imagefile?: Artifact,
    storytellerName?: string,
    source?: Record<string, any>,
    parentCompositionId?: string,
    metadata?: Record<string, any>,
    speakerId?: string,
  ) {
    const itemType = await this.dClient.itemTypes.find('shareable_image');
    const savedShareableImage = await this.dClient.items.create({
      item_type: { type: 'item_type', id: itemType!.id },
      story_id: story.id,
      title: `${story.title} - ${story.id}`,
      quote,
      storyteller_name: storytellerName || story.storyTeller?.name,
      source: JSON.stringify(source),
      metadata: JSON.stringify(metadata),
      ...(imagefile && {
        imagefile: {
          upload_id: imagefile.id,
        },
      }),
      ...(parentCompositionId && {
        parent_composition: parentCompositionId,
      }),
      ...(speakerId && { speaker: speakerId }),
    });
    return savedShareableImage.id;
  }

  public async updateShareableImage(
    id: string,
    quote: string,
    imagefile?: Artifact,
    storytellerName?: string,
    source?: Record<string, any>,
    parentCompositionId?: string,
    metadata?: Record<string, any>,
    speakerId?: string,
  ) {
    await this.dClient.items.update(id, {
      quote,
      storyteller_name: storytellerName,
      source: JSON.stringify(source),
      metadata: JSON.stringify(metadata),
      ...(imagefile && {
        imagefile: {
          upload_id: imagefile.id,
          alt: imagefile.responsiveImage?.alt,
        },
      }),
      ...(parentCompositionId && {
        parent_composition: parentCompositionId,
      }),
      ...(speakerId && { speaker: speakerId }),
    });
    return id;
  }

  async deleteTranscription(storyId: string) {
    await this.dClient.items.update(storyId, {
      locked: false,
      transcription: null,
    });
    await this.dClient.items.publish(storyId);
  }
}
